// src/hooks/useOrderPolling.js
// Polls GET /api/orders/:orderId every 3 seconds after payment.
// Stops when: shipment_id appears | permanent_failed | delivered | 5 min elapsed.
// See docs/briefs/FRONTEND_INTEGRATION_GUIDE.md §6 for full strategy.

import { useState, useEffect, useRef, useCallback } from 'react';
import { getOrderById } from '../services/orderService';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * @param {string|null} orderId       - The order ID to poll
 * @param {string|null} idToken       - Firebase ID token string (static — for backwards compat)
 * @param {Object}      options
 * @param {boolean}     options.enabled          - Set false to pause polling (default: true)
 * @param {Function}    options.getIdToken       - Async fn that returns a fresh token (preferred over static idToken)
 * @param {Function}    options.onShipmentReady  - Called when shipment_id first appears
 * @param {Function}    options.onPermanentFail  - Called when status = permanent_failed
 * @returns {{ order, isPolling, pollingSeconds, stopPolling }}
 */
export default function useOrderPolling(orderId, idToken, options = {}) {
  const { enabled = true, getIdToken, onShipmentReady, onPermanentFail } = options;

  const [order, setOrder] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingSeconds, setPollingSeconds] = useState(0);

  const startTimeRef = useRef(null);
  const timerRef = useRef(null);
  const secondsTimerRef = useRef(null);
  const hasCalledShipmentReady = useRef(false);
  const hasCalledPermanentFail = useRef(false);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
    clearTimeout(timerRef.current);
    clearInterval(secondsTimerRef.current);
  }, []);

  const shouldStop = useCallback((orderData) => {
    if (!orderData) return false;
    const { shiprocket, status } = orderData;
    if (shiprocket?.shipment_id) return true;
    if (shiprocket?.status === 'permanent_failed') return true;
    if (status === 'delivered') return true;
    return false;
  }, []);

  useEffect(() => {
    if (!orderId || !enabled) return;

    startTimeRef.current = Date.now();
    setIsPolling(true);
    hasCalledShipmentReady.current = false;
    hasCalledPermanentFail.current = false;

    setPollingSeconds(0);
    secondsTimerRef.current = setInterval(() => {
      setPollingSeconds(s => s + 1);
    }, 1000);

    const poll = async () => {
      if (Date.now() - startTimeRef.current >= MAX_POLL_DURATION_MS) {
        stopPolling();
        return;
      }

      try {
        // Prefer async getIdToken (auto-refresh) over static token
        const token = getIdToken ? await getIdToken() : idToken;
        const data = await getOrderById(orderId, token);
        if (!data) return;

        setOrder(data);

        if (data.shiprocket?.shipment_id && !hasCalledShipmentReady.current) {
          hasCalledShipmentReady.current = true;
          onShipmentReady?.(data);
        }
        if (data.shiprocket?.status === 'permanent_failed' && !hasCalledPermanentFail.current) {
          hasCalledPermanentFail.current = true;
          onPermanentFail?.(data);
        }

        if (shouldStop(data)) {
          stopPolling();
          return;
        }
      } catch (err) {
        console.error('[useOrderPolling] fetch error:', err);
      }

      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      clearTimeout(timerRef.current);
      clearInterval(secondsTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, idToken, enabled]);

  return { order, isPolling, pollingSeconds, stopPolling };
}
